import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dataService } from '../../../services/dataService';
import { LearningPath, Question, Standard } from '../../../data/types';
import Button from '../../ui/Button';
import Badge from '../../ui/Badge';
import { AuthContext } from '../../../App';
import { motion } from 'framer-motion';
import { GameIcons } from '../../styles/GameIcons';
import '../../styles/GameStyleUI.css';

// Define CSS animation for falling confetti
const confettiStyle = `
  @keyframes fall {
    0% { transform: translateY(-10px) rotate(0deg); }
    100% { transform: translateY(100vh) rotate(360deg); }
  }

  @keyframes bounce {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
  }
`;

const LearningPhase: React.FC = () => {
  const { pathId, standardId } = useParams<{ pathId: string; standardId: string }>();
  const navigate = useNavigate();
  const { activeRole } = React.useContext(AuthContext);
  
  // State to show the gamified version banner
  const [showGamifiedOption, setShowGamifiedOption] = useState(true);
  
  const [currentPath, setCurrentPath] = useState<LearningPath | null>(null);
  const [currentStandard, setCurrentStandard] = useState<Standard | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [isAnswerCorrect, setIsAnswerCorrect] = useState<boolean | null>(null);
  const [showVideo, setShowVideo] = useState(true);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [attemptsCount, setAttemptsCount] = useState(0);

  // Set student session flag when component mounts
  useEffect(() => {
    // Always set the student session flag to true when accessing this page
    localStorage.setItem('isStudentSession', 'true');
    
    // Only redirect if explicitly authenticated as a non-student role
    // But don't redirect if there's no active role or if the role is Student
    // This prevents incorrect redirections when a student is properly logged in
    if (activeRole && activeRole !== 'Student') {
      navigate('/student/login');
      return;
    }
  }, [navigate, activeRole]);
  
  // Make sure we're not redirecting during data fetching
  useEffect(() => {
    // Ensure the student session flag is set
    if (!localStorage.getItem('isStudentSession')) {
      localStorage.setItem('isStudentSession', 'true');
    }
  }, []);

  // Fetch learning path data
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!pathId || !standardId) return;
        
        // Fetch learning path
        const paths = await dataService.getLearningPaths();
        const path = paths.find(p => p.id === pathId);
        setCurrentPath(path || null);
        
        // Fetch standard details
        const standards = await dataService.getStandards();
        const standard = standards.find(s => s.id === standardId);
        setCurrentStandard(standard || null);
        
        // Fetch questions for this standard
        const allQuestions = await dataService.getQuestions();
        const standardQuestions = allQuestions.filter(q => q.standardId === standardId);
        
        // Sort by difficulty (easy first)
        const sortedQuestions = [...standardQuestions].sort((a, b) => {
          const difficultyOrder = { 'Easy': 1, 'Medium': 2, 'Hard': 3 };
          return difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty];
        });
        
        setQuestions(sortedQuestions);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching learning phase data:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [pathId, standardId]);

  const handleAnswerSelect = (answer: string) => {
    setSelectedAnswer(answer);
  };

  const handleSubmitAnswer = () => {
    if (!selectedAnswer || currentQuestionIndex >= questions.length) return;
    
    const currentQuestion = questions[currentQuestionIndex];
    const correct = selectedAnswer === currentQuestion.correctAnswer;
    
    setIsAnswerCorrect(correct);
    setAttemptsCount(prev => prev + 1);
    
    if (correct) {
      // Wait 1.5 seconds before moving to next question
      setTimeout(() => {
        setSelectedAnswer(null);
        setIsAnswerCorrect(null);
        setShowHint(false);
        setAttemptsCount(0);
        
        if (currentQuestionIndex < questions.length - 1) {
          setCurrentQuestionIndex(prev => prev + 1);
          // Update progress
          const newProgress = ((currentQuestionIndex + 1) / questions.length) * 100;
          setProgress(newProgress);
        } else {
          // All questions completed
          navigate(`/student/learning-results/${pathId}/${standardId}`);
        }
      }, 1500);
    }
  };

  const handleSkipToGame = () => {
    navigate(`/student/learning-results/${pathId}/${standardId}`);
  };

  const handleShowHint = () => {
    setShowHint(true);
  };

  const handleVideoComplete = () => {
    setShowVideo(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-indigo-400 via-purple-300 to-blue-400">
        <div className="text-center p-8 bg-white bg-opacity-90 rounded-xl shadow-lg">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-indigo-800 font-medium text-lg">Loading your learning adventure...</p>
          <div className="mt-4 flex justify-center space-x-2">
            {[...Array(3)].map((_, i) => (
              <div 
                key={i} 
                className="w-3 h-3 rounded-full bg-indigo-600 opacity-75"
                style={{
                  animation: `bounce 1.4s infinite ease-in-out both`,
                  animationDelay: `${i * 0.16}s`
                }}
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4 relative">
      {/* Add the confetti animation style */}
      <style>{confettiStyle}</style>
      
      {/* Decorative elements similar to StudentDashboardV4 */}
      <div className="absolute top-10 left-10 w-16 h-16 bg-yellow-300 rounded-full opacity-20 animate-bounce hidden md:block"></div>
      <div className="absolute bottom-20 right-1/4 w-14 h-14 bg-blue-300 rounded-full opacity-20 animate-pulse hidden md:block"></div>
      <div className="absolute top-1/4 right-10 w-10 h-10 bg-purple-300 rounded-full opacity-20 animate-pulse hidden md:block"></div>
      
      {/* Main content with game-style UI */}
      <div className="max-w-5xl mx-auto relative z-10">
        {/* Gamified Learning Experience Banner */}
        {showGamifiedOption && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 text-white relative rounded-xl shadow-lg mb-6 overflow-hidden"
          >
            {/* Decorative elements */}
            <div className="absolute -right-8 -top-8 w-24 h-24 bg-white opacity-10 rounded-full"></div>
            <div className="absolute right-20 bottom-2 w-12 h-12 bg-white opacity-10 rounded-full"></div>
            
            <div className="flex justify-between items-center relative z-10">
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-1 flex items-center">
                  <GameIcons.Star size={24} className="mr-2" color="#FFD700" />
                  Try Our Gamified Learning Experience!
                </h3>
                <p className="text-sm text-indigo-100">Unlock achievements, earn points, and track your progress in a fun, game-like environment.</p>
              </div>
              <div className="flex space-x-2">
                <button 
                  onClick={() => navigate(`/student/dashboardv4`)}
                  className="px-4 py-2 bg-white text-indigo-600 rounded-md hover:bg-indigo-50 transition-colors font-medium text-sm flex items-center"
                >
                  <GameIcons.Home size={18} className="mr-2" color="#4F46E5" />
                  Go to Game Dashboard
                </button>
                <button 
                  onClick={() => setShowGamifiedOption(false)}
                  className="p-2 text-white hover:bg-indigo-400 rounded-md transition-colors"
                  aria-label="Dismiss"
                >
                  <GameIcons.X size={20} color="#FFFFFF" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
                {showGamifiedOption && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 text-white relative rounded-xl shadow-lg mb-6 overflow-hidden"
          >
            {/* Decorative elements */}
            <div className="absolute -right-8 -top-8 w-24 h-24 bg-white opacity-10 rounded-full"></div>
            <div className="absolute right-20 bottom-2 w-12 h-12 bg-white opacity-10 rounded-full"></div>
            
            <div className="flex justify-between items-center relative z-10">
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-1 flex items-center">
                  <GameIcons.Star size={24} className="mr-2" color="#FFD700" />
                  Try Our Gamified Learning Experience!
                </h3>
                <p className="text-sm text-indigo-100">Unlock achievements, earn points, and track your progress in a fun, game-like environment.</p>
              </div>
              <div className="flex space-x-2">
                <button 
                  onClick={() => navigate(`/student/dashboardv4`)}
                  className="px-4 py-2 bg-white text-indigo-600 rounded-md hover:bg-indigo-50 transition-colors font-medium text-sm flex items-center"
                >
                  <GameIcons.Home size={18} className="mr-2" color="#4F46E5" />
                  Go to Game Dashboard
                </button>
                <button 
                  onClick={() => setShowGamifiedOption(false)}
                  className="p-2 text-white hover:bg-indigo-400 rounded-md transition-colors"
                  aria-label="Dismiss"
                >
                  <GameIcons.X size={20} color="#FFFFFF" />
                </button>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Learning Path and Standard Information */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="bg-white rounded-xl shadow-lg overflow-hidden mb-6 border-2 border-indigo-100"
      >
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute -right-10 -top-10 w-40 h-40 bg-white opacity-10 rounded-full"></div>
          <div className="absolute left-1/2 bottom-0 w-24 h-24 bg-white opacity-10 rounded-full"></div>
          
          <div className="relative z-10">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
              <div>
                <h2 className="text-2xl font-bold text-white mb-1 flex items-center">
                  <GameIcons.Book size={28} className="mr-2" color="#FFFFFF" />
                  {currentPath?.name || 'Learning Path'}
                </h2>
                <div className="flex items-center">
                  <Badge className="mr-2 bg-white bg-opacity-20 text-white border-none">
                    {currentStandard?.grade} Grade
                  </Badge>
                  <span className="text-blue-100">{currentStandard?.code}</span>
                </div>
              </div>
              <button 
                onClick={() => navigate(`/student/dashboardv4`)}
                className="mt-2 sm:mt-0 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg transition-colors flex items-center"
              >
                <GameIcons.ChevronLeft size={18} className="mr-1" color="#FFFFFF" />
                Back to Dashboard
              </button>
            </div>
            
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white mb-2">Standard</h3>
              <p className="text-blue-100">{currentStandard?.description}</p>
            </div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="px-6 py-4 border-b border-indigo-100">
          <div className="flex justify-between text-sm text-indigo-700 mb-2">
            <span className="font-medium flex items-center">
              <svg className="mr-2" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 17H13V15H3V17ZM3 12H21V10H3V12ZM3 7H21V5H3V7Z" fill="#4F46E5"/>
              </svg>
              Progress
            </span>
            <span className="font-bold ml-auto">{currentQuestionIndex}/{questions.length} Questions</span>
          </div>
          <div className="w-full bg-indigo-100 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 rounded-full transition-all duration-300 ease-in-out relative"
              style={{ width: `${progress}%` }}
            >
              {/* Animated pulse effect on progress bar */}
              <span className="absolute top-0 right-0 -mr-1 -mt-1 w-5 h-5 rounded-full bg-white border-2 border-indigo-500 transform scale-100 animate-pulse"></span>
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Learning Content */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-indigo-100"
      >
        <div className="p-6">
          {loading ? (
            <div className="flex flex-col justify-center items-center py-12">
              <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
              <p className="text-indigo-600 font-medium">Loading your learning content...</p>
            </div>
          ) : showVideo ? (
            <div className="mb-6">
              <h3 className="game-heading mb-4 relative flex items-center">
                <svg className="mr-2" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3ZM12 19C8.13 19 5 15.87 5 12C5 8.13 8.13 5 12 5C15.87 5 19 8.13 19 12C19 15.87 15.87 19 12 19Z" fill="#4F46E5"/>
                  <path d="M12 17C14.7614 17 17 14.7614 17 12C17 9.23858 14.7614 7 12 7C9.23858 7 7 9.23858 7 12C7 14.7614 9.23858 17 12 17Z" fill="#4F46E5"/>
                </svg>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 font-bold text-xl">
                  Watch the Lesson
                </span>
                <div className="absolute -bottom-1 left-0 w-full h-1 bg-indigo-500 rounded-full"></div>
              </h3>
              <div className="aspect-w-16 aspect-h-9 bg-gray-100 rounded-lg overflow-hidden border-2 border-indigo-100">
                <iframe 
                  src="https://www.youtube.com/embed/dQw4w9WgXcQ" 
                  title="Lesson Video"
                  className="w-full h-full"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="mt-6 flex justify-end">
                <button 
                  onClick={handleVideoComplete} 
                  className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg shadow-md flex items-center justify-center transition-all duration-200 transform hover:scale-105"
                >
                  <span className="mr-2">Continue to Practice</span>
                  <GameIcons.ChevronRight size={20} color="#FFFFFF" />
                </button>
              </div>
            </div>
          ) : currentQuestionIndex < questions.length ? (
            <div>
              <h3 className="game-heading mb-4 relative flex items-center">
                <svg className="mr-2" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3ZM12 19C8.13 19 5 15.87 5 12C5 8.13 8.13 5 12 5C15.87 5 19 8.13 19 12C19 15.87 15.87 19 12 19Z" fill="#4F46E5"/>
                  <path d="M12 17C14.7614 17 17 14.7614 17 12C17 9.23858 14.7614 7 12 7C9.23858 7 7 9.23858 7 12C7 14.7614 9.23858 17 12 17Z" fill="#4F46E5"/>
                </svg>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 font-bold text-xl">
                  Practice Question {currentQuestionIndex + 1} of {questions.length}
                </span>
                <div className="absolute -bottom-1 left-0 w-full h-1 bg-indigo-500 rounded-full"></div>
              </h3>
              
              <div className="p-5 bg-indigo-50 rounded-lg mb-6 border border-indigo-100 shadow-sm">
                <p className="text-indigo-800 font-medium text-lg">{questions[currentQuestionIndex]?.text}</p>
              </div>
              
              <div className="flex flex-col space-y-3">
                {questions[currentQuestionIndex]?.options?.map((option, index) => {
                  // Handle both string and StringOption types
                  const optionText = typeof option === 'string' ? option : option.text;
                  const optionImage = typeof option === 'string' ? undefined : option.image;
                  
                  return (
                    <motion.div 
                      key={index}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`flex items-center p-4 rounded-lg cursor-pointer border-2 transition-all duration-200 ${selectedAnswer === optionText 
                        ? 'bg-indigo-100 border-indigo-400 shadow-md' 
                        : 'border-gray-200 hover:border-indigo-200 hover:bg-indigo-50'}`}
                      onClick={() => handleAnswerSelect(optionText)}
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 font-bold ${selectedAnswer === optionText 
                        ? 'bg-indigo-500 text-white' 
                        : 'bg-gray-100 text-gray-700'}`}>
                        {String.fromCharCode(65 + index)}
                      </div>
                      <span className="text-gray-800 flex items-center flex-1">
                        {optionImage && (
                          <img src={optionImage} alt="Option visual" className="w-8 h-8 object-contain mr-2 inline-block" />
                        )}
                        <span className="font-medium">{optionText}</span>
                      </span>
                    </motion.div>
                  );
                })}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button 
                  disabled={!selectedAnswer} 
                  onClick={handleSubmitAnswer} 
                  className={`px-6 py-3 rounded-lg flex-grow flex items-center justify-center shadow-md transition-all duration-200 ${!selectedAnswer 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white transform hover:scale-105'}`}
                >
                  <GameIcons.Check size={20} className="mr-2" color={!selectedAnswer ? '#9CA3AF' : '#FFFFFF'} />
                  Submit Answer
                </button>

                {!showHint && attemptsCount > 0 && (
                  <button 
                    onClick={handleShowHint} 
                    className="px-6 py-3 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors flex items-center justify-center"
                  >
                    <GameIcons.LightBulb size={20} className="mr-2" color="#B45309" />
                    Show Hint
                  </button>
                )}
              </div>

                {isAnswerCorrect === false && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start"
                  >
                    <svg className="mr-3 flex-shrink-0 mt-1" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM12 20C16.418 20 20 16.418 20 12C20 7.582 16.418 4 12 4C7.582 4 4 7.582 4 12C4 16.418 7.582 20 12 20ZM11 15H13V17H11V15ZM11 7H13V13H11V7Z" fill="#DC2626"/>
                    </svg>
                    <p className="text-red-700">That's not quite right. Try again or use the hint for help!</p>
                  </motion.div>
                )}

                {showHint && questions[currentQuestionIndex] && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg"
                  >
                    <div className="flex items-start">
                      <GameIcons.LightBulb size={20} className="mr-3 flex-shrink-0 mt-1" color="#B45309" />
                      <div>
                        <div className="font-bold text-amber-700 mb-1">Hint:</div>
                        <p className="text-amber-800">{questions[currentQuestionIndex]?.hints?.[0] || 'Think carefully about the question.'}</p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {isAnswerCorrect === true && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start"
                  >
                    <GameIcons.CheckCircle size={20} className="mr-3 flex-shrink-0 mt-1" color="#10B981" />
                    <p className="text-green-700">Great job! That's correct!</p>
                  </motion.div>
                )}
              </div>
            ) : (
              <div className="p-8 text-center relative">
                {/* Celebration confetti effect */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  {[...Array(20)].map((_, i) => (
                    <div 
                      key={i}
                      className="absolute w-2 h-2 rounded-full"
                      style={{
                        backgroundColor: ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EC4899'][i % 5],
                        top: `${Math.random() * 100}%`,
                        left: `${Math.random() * 100}%`,
                        opacity: Math.random() * 0.7 + 0.3,
                        animation: `fall ${Math.random() * 3 + 2}s linear infinite`,
                        animationDelay: `${Math.random() * 2}s`,
                        transform: `rotate(${Math.random() * 360}deg)`
                      }}
                    />
                  ))}
                </div>
                
                <div className="w-28 h-28 mx-auto bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mb-6 shadow-lg border-4 border-white">
                  <GameIcons.Trophy size={56} color="#FFFFFF" />
                </div>
                <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-teal-600 mb-3">Practice Complete!</h3>
                <p className="text-gray-600 mb-8 max-w-md mx-auto text-lg">You've completed all the practice questions for this standard. Now it's time to reinforce your learning with some fun games!</p>
                
                <div className="relative inline-block">
                  {/* Pulsing glow effect behind button */}
                  <div className="absolute inset-0 bg-indigo-400 rounded-lg filter blur-md animate-pulse"></div>
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleSkipToGame} 
                    className="relative px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg shadow-md flex items-center mx-auto text-lg font-bold"
                  >
                    <GameIcons.Play size={24} className="mr-3" color="#FFFFFF" />
                    Play Games
                  </motion.button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LearningPhase;
